import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ScrollView } from 'react-native'
import styled from 'styled-components/native'
import moment, { Moment } from 'moment'
import { AvailabilitySlot } from '@dialogue/timekeeper'

import AppointmentBookingRedux, { DATE_FORMAT } from 'APP/Redux/AppointmentBookingRedux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { providerBookingActions } from 'APP/Store/ProviderBooking'
import I18n from 'APP/I18n'

import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { useTimeBasedAvailabilities } from 'APP/Hooks/useGetAvailabilities'

import { FEE_TYPES, isOCAppointment } from 'APP/Lib/AppointmentManagementHelpers'

import type { BookingFlowProps } from 'APP/Components/AppointmentBooking/AppointmentBookingBase/types'
import { CreateAppointmentPayload } from 'APP/Store/AppointmentBooking/types'
import { CreatedAppointment } from 'APP/Components/AppointmentBooking/utils/types'
import { TimeslotSelection } from 'APP/Components/AppointmentBooking/TimeBased/timeslotSelection'
import ExpiredSchedule from 'APP/Components/AppointmentBooking/common/expiredSchedule'
import { Checkout, CHECKOUT_TYPE } from 'APP/Components/Checkout'
import BottomDrawer from 'APP/Converse/BottomDrawer'
import Button from 'APP/Converse/Button'

import { Camelized, camelizeKeys } from 'humps'

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`

const ConfirmationWrapper = styled.View`
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: 0px -8px 8px ${({ theme }) => theme.colors.background};
  elevation: 4;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin + theme.insets.bottom}px;
`

const TimeAppointmentBooking = (props: BookingFlowProps) => {
  const dispatch = useDispatch()
  const {
    appointment_id,
    is_rescheduling,
    close,
    onComplete,
    onError,
    appointment_type,
    booking_id,
    date_from,
    date_until,
    disabled,
    booking,
    isLoadingBooking,
    isBookingReady,
    customHeaderText,
  } = props

  const isAppointmentCreatedRef = useRef(false)
  const {
    availabilities,
    availabilitiesExists,
    selectedDay,
    selectedAppointment,
    createAppointmentRequest,
  } = useSelector((state) => state.appointmentBooking)

  const { cancelAppointmentFromCalendarSuccess } = useSelector((state) => state.history)

  const {
    data: createdAppointmentData,
    error: appointmentCreationError,
    isLoading: isCreatingAppointment,
  } = createAppointmentRequest

  const dateFrom = useMemo(() => moment(date_from), [date_from])
  const dateUntil = useMemo(() => moment(date_until), [date_until])
  const selectedDayMoment = useMemo(() => moment(selectedDay, DATE_FORMAT), [selectedDay])
  const isExpired = dateUntil.isBefore(moment())

  const { handleGetAvailabilities } = useTimeBasedAvailabilities(booking_id, date_from)

  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false)
  const [showPaymentScreen, setShowPaymentScreen] = useState(false)

  const handleSelectDay = useCallback(
    (day: Moment) => {
      dispatch(AppointmentBookingRedux.selectDay(day.format(DATE_FORMAT)))

      handleGetAvailabilities(day)
    },
    [dispatch, handleGetAvailabilities]
  )

  const handleSelectAppointment = useCallback(
    (slot: AvailabilitySlot) => {
      dispatch(AppointmentBookingRedux.selectAppointment(slot))
    },
    [dispatch]
  )

  const handleCreateAppointment = useCallback(() => {
    if (selectedAppointment) {
      const payload: CreateAppointmentPayload = {
        bookingId: booking_id,
        start_at: selectedAppointment.start_at,
        end_at: selectedAppointment.end_at,
        provider_id: selectedAppointment.provider_id,
      }
      dispatch(AppointmentBookingRedux.createAppointment(payload))
    }
  }, [dispatch, booking_id, selectedAppointment])

  const handleConfirmAppointment = useCallback(() => {
    if (!isBookingReady) return

    // TODO: Update this logic once a decision is made via CLI-897
    if (
      booking?.dialogue_metadata[FEE_TYPES.ENCOUNTER_FEE] > 0 &&
      isOCAppointment(appointment_type)
    ) {
      setShowPaymentScreen(true)
    } else {
      handleCreateAppointment()
    }
  }, [isBookingReady, booking, handleCreateAppointment])

  const onSuccessAppointmentCreated = useCallback(() => {
    const displayMessage = moment.tz(selectedAppointment?.start_at, moment.tz.guess()).format('LLL')

    const payload: CreatedAppointment = {
      is_booked: true,
      ...{
        ...selectedAppointment,
        until_at: selectedAppointment?.end_at,
        // Since service_id is required in BM payload and we don't have it in timekeeper response, we set it as -1
        service_id: -1,
      },
    }

    onComplete({ display: displayMessage, payload }, createdAppointmentData ?? undefined)
    close?.()
  }, [selectedAppointment, createdAppointmentData, onComplete, close])

  const onAppointmentCreationError = useCallback(() => {
    setShowPaymentScreen(false)
    setIsErrorModalVisible(true)
    onError?.(appointmentCreationError)
    dispatch(AppointmentBookingRedux.resetCreateAppointment())
  }, [dispatch, onError, appointmentCreationError])

  const quitScheduling = useCallback(() => {
    if (is_rescheduling) {
      dispatch(
        PatientHistoryActions.cancelAppointmentFromCalendarRequest({
          id: appointment_id!.replace('-', ''), // ids are negative
        })
      )
    } else {
      onComplete({
        display: I18n.t('AppointmentBooking.get_help_scheduling_later'),
        payload: { is_booked: false },
      })
      close?.()
    }
  }, [is_rescheduling, appointment_id, dispatch, close, onComplete])

  // resets selected appointment on each day selection and initial render
  useEffect(() => {
    dispatch(AppointmentBookingRedux.selectAppointment(null))
  }, [selectedDay, dispatch])

  useEffect(() => {
    handleSelectDay(moment(date_from))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date_from])

  useEffect(() => {
    if (createdAppointmentData && !isCreatingAppointment && !isAppointmentCreatedRef.current) {
      isAppointmentCreatedRef.current = true
      onSuccessAppointmentCreated()
    }
  }, [createdAppointmentData, isCreatingAppointment, onSuccessAppointmentCreated])

  useEffect(() => {
    if (appointmentCreationError && !isCreatingAppointment) {
      onAppointmentCreationError()
    }
  }, [appointmentCreationError, isCreatingAppointment, onAppointmentCreationError])

  useEffect(() => {
    if (cancelAppointmentFromCalendarSuccess) {
      onComplete({
        display: I18n.t('AppointmentBooking.get_help_scheduling_later'),
        payload: { is_booked: false },
      })
      close?.()
      dispatch(PatientHistoryActions.resetCancelAppointmentFromCalendar())
    }
  }, [cancelAppointmentFromCalendarSuccess, dispatch, close, onComplete])

  useEffect(() => {
    if (selectedAppointment?.provider_id) {
      dispatch(providerBookingActions.getProviders([selectedAppointment.provider_id]))
    }
  }, [selectedAppointment?.provider_id, dispatch])

  const handleErrorModalClose = useCallback(() => {
    setIsErrorModalVisible(false)
    handleGetAvailabilities(selectedDayMoment)
  }, [handleGetAvailabilities, selectedDayMoment])

  return (
    <Container testID="APPOINTMENT_BOOKING">
      <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
        {showPaymentScreen && booking && selectedAppointment && (
          <Checkout
            type={CHECKOUT_TYPE.APPOINTMENT}
            context={{
              availabilitySlot: camelizeKeys(selectedAppointment) as Camelized<AvailabilitySlot>,
            }}
            // TODO: Remove indexed support once timekeeper migration is complete
            // Reference: https://github.com/dialoguemd/member-app/pull/7445#discussion_r3768109545
            feePolicy={{
              encounterFeeAmount:
                booking.dialogue_metadata[FEE_TYPES.ENCOUNTER_FEE][0] ||
                booking.dialogue_metadata[FEE_TYPES.ENCOUNTER_FEE],
              memberLateCancellationAmount:
                booking.dialogue_metadata[FEE_TYPES.LATE_CANCELLATION][0] ||
                booking.dialogue_metadata[FEE_TYPES.LATE_CANCELLATION],
              memberNoShowAmount:
                booking.dialogue_metadata[FEE_TYPES.NO_SHOW][0] ||
                booking.dialogue_metadata[FEE_TYPES.NO_SHOW],
            }}
            onConfirm={async () => {
              handleCreateAppointment()
            }}
          />
        )}

        {!showPaymentScreen &&
          (isExpired ? (
            <ExpiredSchedule quitScheduling={quitScheduling} />
          ) : (
            <TimeslotSelection
              availabilities={availabilities}
              availabilitiesExists={availabilitiesExists}
              dateFrom={dateFrom}
              dateUntil={dateUntil}
              selectedDay={selectedDayMoment}
              selectDay={handleSelectDay}
              selectedAppointment={selectedAppointment}
              selectAppointment={handleSelectAppointment}
              quitScheduling={quitScheduling}
              hasExistingAppointment={is_rescheduling}
              appointmentType={appointment_type}
              customHeaderText={customHeaderText}
            />
          ))}
      </ScrollView>

      {!showPaymentScreen && selectedAppointment && (
        <ConfirmationWrapper testID="APPOINTMENT_BOOKING_CONFIRMATION">
          <Button
            variant="primary"
            widthVariant="full"
            title={I18n.t('AppointmentBooking.confirm_appointment')}
            testID="APPOINTMENT_BOOKING_CONFIRM_APPOINTMENT_BTN"
            onPress={handleConfirmAppointment}
            disabled={
              disabled ||
              isCreatingAppointment ||
              Boolean(appointmentCreationError) ||
              !isBookingReady ||
              isLoadingBooking
            }
          />
        </ConfirmationWrapper>
      )}

      <BottomDrawer
        isVisible={isErrorModalVisible}
        onClose={handleErrorModalClose}
        closeButtonProps={{ hidden: true }}
        title={I18n.t('AppointmentBooking.book_appointment_error.title')}
        subtitle={I18n.t('AppointmentBooking.book_appointment_error.subtitle')}
        primaryButtonProps={{
          title: I18n.t('AppointmentBooking.book_appointment_error.cta'),
          onPress: handleErrorModalClose,
          widthVariant: 'full',
          small: false,
          analyticsName: 'appointmentBooking.appointmentCreationError.seeNextAvailableTimes',
        }}
      />
    </Container>
  )
}

export default TimeAppointmentBooking
